import React from 'react';

/**
 * Where a lead came from, as a badge small enough for the dashboard's lead rows.
 *
 * The Seller Leads page has a whole column and spells the source out in full
 * ("Meta (Facebook/Instagram)"). Here there is room for one word beside the
 * stage pill, so the labels are deliberately short.
 *
 * Meta is picked out in Facebook blue rather than the neutral grey. That is the
 * point of the badge: paid Facebook lead ads are the feed the agency spends
 * money on, and until now nothing on the dashboard said which leads it bought
 * (see the lead_ingest intake API). Every other known source still gets a badge
 * so the row reads consistently, just without shouting.
 */
const SOURCE_LABELS = {
  meta: 'Meta',
  social_media: 'Social',
  social: 'Social',
  rea: 'REA',
  realestate: 'REA',
  domain: 'Domain',
  website: 'Website',
  referral: 'Referral',
  cold_call: 'Cold Call',
  ofi: 'OFI',
  chatbot: 'Chatbot',
  buyer_portal: 'Portal',
  voiceCaller: 'Voice',
  form: 'Form',
  direct: 'Direct',
  other: 'Other',
  // Moved back onto Prospects from the Database (to_pipeline.py, 20 Oct 2026).
  database: 'Database',
  relisting: 'Re-listing',
  // Typed in from Add Lead (DOMA 13375194285).
  google: 'Google',
  ai_chat: 'AI',
};

// Spelled out on hover, because "OFI" and "REA" mean nothing to a new starter.
const SOURCE_TITLES = {
  meta: 'Meta (Facebook / Instagram) lead ad',
  rea: 'realestate.com.au',
  realestate: 'realestate.com.au',
  domain: 'domain.com.au',
  ofi: 'Open For Inspection',
  buyer_portal: 'Buyer Portal',
  voiceCaller: 'Voice Caller',
  relisting: 'Re-listing Opportunity',
  ai_chat: 'ChatGPT / Claude / Gemini / Grok',
};

const LeadSourceBadge = ({ source, className = '' }) => {
  const key = (source || '').trim();
  const label = SOURCE_LABELS[key];
  // An unrecognised or empty source shows nothing rather than a badge reading
  // "undefined". A lead is never worth less because we cannot label it.
  if (!label) return null;

  const isMeta = key === 'meta';
  const tone = isMeta
    ? 'bg-[#E7F0FE] text-[#1877F2]'
    : 'bg-gray-100 text-gray-600';

  return (
    <span
      title={SOURCE_TITLES[key] || label}
      className={`px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] rounded font-medium whitespace-nowrap ${tone} ${className}`}
    >
      {label}
    </span>
  );
};

export default LeadSourceBadge;
